import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, X, Bookmark, MapPin, Sparkles, Loader2, ShieldCheck, Globe2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { useAuth } from '../lib/AuthContext';

function formatLanguages(langs) {
  if (!Array.isArray(langs)) return '';
  return langs.slice(0, 3).join(' · ');
}

function ProfileCard({ profile, onSwipe, isTop }) {
  const [photoIdx, setPhotoIdx] = useState(0);
  const photos = profile.photos?.length ? profile.photos : [];
  const photo = photos[photoIdx] || '';

  function nextPhoto(e) {
    e.stopPropagation();
    if (photos.length > 1) setPhotoIdx((i) => (i + 1) % photos.length);
  }

  function prevPhoto(e) {
    e.stopPropagation();
    if (photos.length > 1) setPhotoIdx((i) => (i - 1 + photos.length) % photos.length);
  }

  return (
    <motion.div
      data-testid={`discover-card-${profile.id}`}
      drag={isTop ? 'x' : false}
      dragConstraints={{ left: 0, right: 0 }}
      onDragEnd={(_, info) => {
        if (info.offset.x > 120) onSwipe('like');
        else if (info.offset.x < -120) onSwipe('pass');
      }}
      whileDrag={{ rotate: 0, scale: 1.02 }}
      className="absolute inset-0 bg-white rounded-3xl overflow-hidden shadow-2xl border border-[#F4F1DE]"
      style={{ touchAction: 'pan-y' }}
    >
      <div className="relative h-3/5 bg-gradient-to-br from-[#FFE5DD] to-[#F4F1DE]">
        {photo ? (
          <img src={photo} alt={profile.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-7xl font-serif font-bold text-[#C8553D]/40" style={{ fontFamily: 'Playfair Display, serif' }}>
            {(profile.name || '?').charAt(0).toUpperCase()}
          </div>
        )}
        {/* Photo navigation strips */}
        {photos.length > 1 && (
          <>
            <div className="absolute top-3 left-3 right-3 flex gap-1">
              {photos.map((_, i) => (
                <div
                  key={i}
                  className={`flex-1 h-1 rounded-full ${i === photoIdx ? 'bg-white' : 'bg-white/30'}`}
                />
              ))}
            </div>
            <button
              onClick={prevPhoto}
              data-testid={`discover-photo-prev-${profile.id}`}
              className="absolute left-0 top-0 bottom-0 w-1/3"
              aria-label="Previous photo"
            />
            <button
              onClick={nextPhoto}
              data-testid={`discover-photo-next-${profile.id}`}
              className="absolute right-0 top-0 bottom-0 w-1/3"
              aria-label="Next photo"
            />
          </>
        )}

        {profile.is_premium && (
          <div className="absolute top-3 right-3 bg-[#E9C46A] px-2 py-1 rounded-full flex items-center gap-1 shadow-md">
            <Sparkles className="w-3 h-3 text-white" />
            <span className="text-[10px] font-bold text-white uppercase tracking-wide">Premium</span>
          </div>
        )}

        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent p-5">
          <div className="flex items-end justify-between">
            <div>
              <h2 className="text-2xl font-serif font-bold text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
                {profile.name}, <span className="font-normal">{profile.age}</span>
              </h2>
              <div className="flex items-center gap-3 mt-1 text-white/90 text-sm">
                {profile.city && (
                  <span className="flex items-center gap-1">
                    <MapPin className="w-3.5 h-3.5" />
                    {profile.city}
                  </span>
                )}
                {profile.country_of_origin && (
                  <span className="flex items-center gap-1">
                    <Globe2 className="w-3.5 h-3.5" />
                    {profile.country_of_origin}
                  </span>
                )}
              </div>
            </div>
            {(profile.badges?.photo_verified || profile.badges?.includes?.('verified')) && (
              <ShieldCheck className="w-6 h-6 text-[#2A9D8F]" />
            )}
          </div>
        </div>
      </div>

      {/* Bio */}
      <div className="p-5 h-2/5 overflow-y-auto">
        {profile.occupation && (
          <p className="text-xs uppercase tracking-wider text-[#C8553D] font-bold mb-1">{profile.occupation}</p>
        )}
        <p className="text-sm text-[#2B2D42] leading-relaxed mb-3">{profile.bio || 'No bio yet.'}</p>
        <div className="flex flex-wrap gap-1.5">
          {profile.languages?.length > 0 && (
            <span className="text-[11px] px-2 py-1 bg-[#F4F1DE] rounded-full text-[#6D6875]">
              {formatLanguages(profile.languages)}
            </span>
          )}
          {profile.religion && (
            <span className="text-[11px] px-2 py-1 bg-[#F4F1DE] rounded-full text-[#6D6875]">{profile.religion}</span>
          )}
          {(profile.relationship_intents || []).slice(0, 2).map((r) => (
            <span key={r} className="text-[11px] px-2 py-1 bg-[#FFE5DD] rounded-full text-[#C8553D] font-semibold">
              {r}
            </span>
          ))}
        </div>
      </div>
    </motion.div>
  );
}

export default function Discover() {
  const { user } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [matchModal, setMatchModal] = useState(null);
  const actingRef = useRef(false);

  async function loadProfiles() {
    setLoading(true);
    try {
      const data = await api('/discover', { method: 'POST', body: JSON.stringify({}) });
      setProfiles(data.profiles || []);
    } catch (err) {
      toast.error(err.message || 'Failed to load profiles');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadProfiles();
  }, []);

  async function handleAction(action) {
    if (actingRef.current || profiles.length === 0) return;
    actingRef.current = true;
    const target = profiles[0];
    setProfiles((p) => p.slice(1));

    try {
      const data = await api('/likes', {
        method: 'POST',
        body: JSON.stringify({ to_user_id: target.id, action }),
      });

      if (data.match_created) {
        setMatchModal({ profile: target, match: data.match });
      } else if (action === 'like') {
        toast.success(`You liked ${target.name}`);
      } else if (action === 'save') {
        toast(`Saved ${target.name} for later`, { icon: '🔖' });
      }
    } catch (err) {
      toast.error(err.message || 'Action failed');
      // Restore the card if it failed
      setProfiles((p) => [target, ...p]);
    } finally {
      actingRef.current = false;
    }
  }

  const top = profiles[0];

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6" data-testid="discover-page">
      {/* Premium upgrade banner (free users only) */}
      {!user?.is_premium && (
        <a
          href="/app/premium"
          data-testid="discover-premium-banner"
          className="block mb-5 bg-gradient-to-r from-[#E9C46A]/30 via-[#E07A5F]/20 to-[#C8553D]/20 border border-[#E9C46A] rounded-2xl px-4 py-3 flex items-center gap-3 hover:scale-[1.01] transition-transform"
        >
          <span className="text-2xl" role="img" aria-label="crown">👑</span>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-sm text-[#2B2D42] truncate">Unlock AfroConnect Premium</p>
            <p className="text-xs text-[#6D6875] truncate">See who likes you · unlimited swipes · priority discovery</p>
          </div>
          <span className="flex-shrink-0 bg-[#C8553D] text-white text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wider">Upgrade</span>
        </a>
      )}

      <div className="text-center mb-4">
        <h1 className="text-3xl font-serif font-bold text-[#2B2D42]" style={{ fontFamily: 'Playfair Display, serif' }}>
          Discover
        </h1>
        <p className="text-sm text-[#6D6875] mt-1">Swipe right to like, left to pass · {profiles.length} profile{profiles.length === 1 ? '' : 's'} ready</p>
      </div>

      <div className="relative max-w-md mx-auto" style={{ height: 'min(70vh, 640px)' }}>
        {loading ? (
          <div className="flex items-center justify-center h-full" data-testid="discover-loading">
            <Loader2 className="w-8 h-8 animate-spin text-[#C8553D]" />
          </div>
        ) : profiles.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center bg-white rounded-3xl border border-[#F4F1DE] p-8" data-testid="discover-empty">
            <div className="w-16 h-16 rounded-full bg-[#FFE5DD] flex items-center justify-center mb-4">
              <Sparkles className="w-8 h-8 text-[#C8553D]" />
            </div>
            <h2 className="text-xl font-serif font-bold text-[#2B2D42] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              You're all caught up!
            </h2>
            <p className="text-sm text-[#6D6875] mb-6">Check back later for more profiles in your area.</p>
            <button
              onClick={loadProfiles}
              data-testid="discover-refresh-button"
              className="bg-[#C8553D] hover:bg-[#9E3A26] text-white font-bold py-3 px-6 rounded-full"
            >
              Refresh
            </button>
          </div>
        ) : (
          <AnimatePresence>
            {profiles.slice(0, 3).reverse().map((p, idx, arr) => {
              const isTop = idx === arr.length - 1;
              return (
                <ProfileCard
                  key={p.id}
                  profile={p}
                  isTop={isTop}
                  onSwipe={handleAction}
                />
              );
            })}
          </AnimatePresence>
        )}
      </div>

      {/* Action buttons */}
      {!loading && top && (
        <div className="flex items-center justify-center gap-4 mt-6">
          <button
            onClick={() => handleAction('pass')}
            data-testid="discover-pass-button"
            className="w-14 h-14 rounded-full bg-white border-2 border-[#F4F1DE] flex items-center justify-center hover:bg-[#FFEBEE] hover:border-[#E76F51] transition-colors shadow-md"
            aria-label="Pass"
          >
            <X className="w-7 h-7 text-[#E76F51]" strokeWidth={2.5} />
          </button>
          <button
            onClick={() => handleAction('save')}
            data-testid="discover-save-button"
            className="w-12 h-12 rounded-full bg-white border-2 border-[#F4F1DE] flex items-center justify-center hover:bg-[#FFF8E1] hover:border-[#E9C46A] transition-colors shadow-md"
            aria-label="Save"
          >
            <Bookmark className="w-5 h-5 text-[#E9C46A]" strokeWidth={2.5} />
          </button>
          <button
            onClick={() => handleAction('like')}
            data-testid="discover-like-button"
            className="w-16 h-16 rounded-full bg-gradient-to-br from-[#E07A5F] to-[#C8553D] flex items-center justify-center hover:scale-105 transition-transform shadow-xl shadow-[#C8553D]/40"
            aria-label="Like"
          >
            <Heart className="w-8 h-8 text-white" fill="white" strokeWidth={2} />
          </button>
        </div>
      )}

      {/* Match modal */}
      <AnimatePresence>
        {matchModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-6"
            data-testid="match-modal"
            onClick={() => setMatchModal(null)}
          >
            <motion.div
              initial={{ scale: 0.8, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              className="bg-white rounded-3xl p-8 max-w-sm w-full text-center"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-6xl mb-3">🎉</div>
              <h2 className="text-3xl font-serif font-bold text-[#C8553D] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                It's a Match!
              </h2>
              <p className="text-[#6D6875] mb-6">You and {matchModal.profile.name} liked each other.</p>
              <div className="flex gap-3">
                <button
                  onClick={() => setMatchModal(null)}
                  data-testid="match-keep-swiping"
                  className="flex-1 bg-[#F4F1DE] text-[#2B2D42] font-bold py-3 rounded-full"
                >
                  Keep Swiping
                </button>
                <Link
                  to={`/app/chat/${matchModal.match.id}`}
                  state={{ otherUser: matchModal.profile, match: matchModal.match }}
                  data-testid="match-send-message"
                  className="flex-1 bg-[#C8553D] text-white font-bold py-3 rounded-full hover:bg-[#9E3A26] transition-colors flex items-center justify-center"
                >
                  Say Hi
                </Link>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
